/* eslint-disable no-unused-vars */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { User } from "@phosphor-icons/react";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", designation: "", bio: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        designation: user.designation || "",
        bio: user.bio || "",
      });
    }
  }, [user]);

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    try {
      await api.put("/me/profile", form);
      setMsg("Profile saved.");
      if (refresh) await refresh();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Failed to save."); }
    finally { setBusy(false); }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setBusy(true); setMsg(""); setErr("");
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsg("Avatar updated.");
      if (refresh) await refresh();
    } catch (er) { setErr(formatApiError(er.response?.data?.detail) || "Upload failed."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="profile-page" className="p-6 max-w-3xl space-y-5">
      <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
        <User size={28} className="text-[#F47D20]" /> My Profile
      </h1>
      <div className="flex items-start gap-6 bg-white p-5 border border-[var(--border)] rounded">
        <div className="text-center">
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="avatar" className="w-24 h-24 rounded-full object-cover ring-2 ring-[#263776]" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-[#263776] text-white text-3xl font-semibold flex items-center justify-center">
              {user?.initials || "U"}
            </div>
          )}
          <label className="block mt-2 cursor-pointer text-xs text-[#263776] hover:underline">
            <input data-testid="avatar-upload" type="file" accept="image/*" onChange={uploadAvatar} className="hidden" />
            Change photo
          </label>
        </div>
        <div className="flex-1 space-y-3">
          {["name","designation","phone","bio"].map(k => (
            <div key={k}>
              <label className="block text-xs text-[var(--text-muted)] mb-1 capitalize">{k}</label>
              {k === "bio" ? (
                <textarea data-testid={`profile-${k}`} value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})}
                  rows={3} className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
              ) : (
                <input data-testid={`profile-${k}`} value={form[k]} onChange={e => setForm({...form, [k]: e.target.value})}
                  className="w-full border border-[var(--border-strong)] rounded px-3 py-2 text-sm" />
              )}
            </div>
          ))}
          <div className="flex gap-3 items-center">
            <button data-testid="profile-save" disabled={busy} onClick={save}
              className="px-5 py-2 bg-[#F47D20] text-white text-sm rounded hover:bg-[#c96009] disabled:opacity-50">
              {busy ? "Saving…" : "Save changes"}
            </button>
            {msg && <span data-testid="profile-msg" className="text-emerald-600 text-sm">{msg}</span>}
            {err && <span data-testid="profile-err" className="text-rose-600 text-sm">{err}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
